import { Divider, FormInstance, Spin } from 'antd';
import { Empty, Key, Form, Input, Modal, Select, Tree, Button, Tag } from '@maxtropy/components';
import type { DataNode } from 'antd/es/tree';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import styles from './index.module.scss';
import _ from 'lodash';
import { EnergyUnitTreeItem, getEnergyUnitTree } from '@/api/energyUnitMapping';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { V2EnergyAnalysisListPostResponse } from '@maxtropy/device-customer-apis-v2';

export type AnalysisUnit = Exclude<V2EnergyAnalysisListPostResponse['list'], undefined>[number];

interface UnitDataNode extends DataNode {
  parentId: string;
}

function formatToNodeData(data: EnergyUnitTreeItem[]): UnitDataNode[] {
  return data.map(i => {
    return {
      title: i.name,
      key: i.energyUnitId,
      children: i.children && i.children.length > 0 ? formatToNodeData(i.children) : [],
      disabled: i.disabled,
      parentId: i.parentId,
    };
  });
}

const dataList: { key: React.Key; title: string }[] = [];

const generateList = (data: DataNode[]) => {
  for (let i = 0; i < data.length; i++) {
    const node = data[i];
    const { key, title } = node;
    dataList.push({ key, title: title as string });
    if (node.children) {
      generateList(node.children);
    }
  }
};

const getParentKey = (key: React.Key, tree: DataNode[]): React.Key => {
  let parentKey: React.Key;
  for (let i = 0; i < tree.length; i++) {
    const node = tree[i];
    if (node.children) {
      if (node.children.some(item => item.key === key)) {
        parentKey = node.key;
      } else if (getParentKey(key, node.children)) {
        parentKey = getParentKey(key, node.children);
      }
    }
  }
  return parentKey!;
};

const getParentIdsByNodeId = (data: DataNode[], nodeId: React.Key, path: React.Key[] = []): React.Key[] => {
  for (const node of data) {
    const id = node.key;
    path.push(node.key);
    if (id === nodeId) return path;
    if (node.children) {
      const findChildren = getParentIdsByNodeId(node.children, nodeId, path);
      if (findChildren?.length) return findChildren;
    }
    path.pop();
  }
  return [];
};

const getIds = (data: DataNode, ids: Key[]) => {
  if (!data.disabled) {
    ids.push(data.key as Key);
  }

  if (data.children) {
    for (const child of data.children) {
      getIds(child, ids);
    }
  }
  return ids;
};

interface unitDataType {
  energyUnitGroupId: number;
  energyUnitList: { energyUnitIds: number; energyUnitName: string }[];
}

interface Props {
  index: number;
  value?: unitDataType | null;
  onChange?: (value: unitDataType | null) => void;
  isEdit?: boolean;
  unitGroup?: AnalysisUnit[];
  form: FormInstance<any>;
}

const BindUnitModal: React.FC<Props> = ({ value, onChange, index, isEdit, unitGroup, form }) => {
  const [innerForm] = Form.useForm();
  const [visible, setVisible] = useState<boolean>(false);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>();
  const [searchValue, setSearchValue] = useState('');
  const [autoExpandParent, setAutoExpandParent] = useState(true);
  const [treeNodeData, setTreeNodeData] = useState<UnitDataNode[]>([]);
  const innerEnergyGroupId = Form.useWatch('energyGroupId', innerForm); // 使用内部 form
  const [loading, setLoading] = useState<boolean>(false);
  const [unitCheckedKeys, setUnitCheckedKeys] = useState<Key[]>(
    value ? value.energyUnitList.map(item => item.energyUnitIds) : []
  ); // 已选中的用能单元

  useEffect(() => {
    if (visible && value?.energyUnitGroupId) {
      innerForm.setFieldValue('energyGroupId', value.energyUnitGroupId);
      setUnitCheckedKeys(value.energyUnitList.map(i => i.energyUnitIds));
    }
  }, [visible, value]);

  const unitGroupOption = useMemo(() => {
    if (unitGroup?.length ?? 0 > 0) {
      return unitGroup?.map(i => ({
        label: i.name,
        value: i.id,
      }));
    }
  }, [unitGroup]);

  const checkAllChildren = (data: DataNode) => {
    const unitIds = getIds(data, []);
    setUnitCheckedKeys(unitCheckedKeys => [...unitCheckedKeys, ...unitIds]);
  };

  const uncheckAllChildren = (data: DataNode) => {
    const unitIds = getIds(data, []);
    setUnitCheckedKeys(unitCheckedKeys => unitCheckedKeys.filter(key => !unitIds.includes(key)));
  };

  const treeData = useMemo(() => {
    const loop = (data: UnitDataNode[]): DataNode[] =>
      data.map(item => {
        const strTitle = item.title as string;
        const index = strTitle.indexOf(searchValue);
        const beforeStr = strTitle.substring(0, index);
        const afterStr = strTitle.slice(index + searchValue.length);
        let title: ReactNode =
          index > -1 ? (
            <span>
              {beforeStr}
              <span className={styles.siteTreeSearchValue}>{searchValue}</span>
              {afterStr}
            </span>
          ) : (
            <span>{strTitle}</span>
          );

        if ((item.parentId as unknown) === 0 && getIds(item, []).length > 1) {
          title = (
            <>
              {title}
              <Button disabled={item.disabled} type="link" size="small" onClick={() => checkAllChildren(item)}>
                全选子节点
              </Button>
              <Button onClick={() => uncheckAllChildren(item)} type="link" size="small">
                取消全选子节点
              </Button>
            </>
          );
        }
        if (item.children) {
          return {
            title,
            key: item.key,
            children: loop(item.children as UnitDataNode[]),
            disabled: item.disabled,
            selectable: true,
          };
        }
        return {
          title,
          key: item.key,
          disabled: item.disabled,
          selectable: true,
        };
      });

    return loop(treeNodeData);
  }, [searchValue, treeNodeData]);

  const onExpand = (newExpandedKeys: React.Key[]) => {
    setExpandedKeys(newExpandedKeys);
    setAutoExpandParent(false);
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const newExpandedKeys = dataList
      .map(item => {
        if (item.title.indexOf(value) > -1) {
          return getParentKey(item.key, treeNodeData);
        }
        return null;
      })
      .filter((item, i, self) => item && self.indexOf(item) === i);
    const allParentKeys = (newExpandedKeys as React.Key[]).map(key => {
      return getParentIdsByNodeId(treeNodeData, key);
    });
    setExpandedKeys(_.uniq(allParentKeys.flat()));
    setSearchValue(value);
    setAutoExpandParent(true);
  };

  const onCheck = (keys: any) => {
    setUnitCheckedKeys(keys.checked);
  };

  useEffect(() => {
    if (innerEnergyGroupId) {
      setLoading(true);
      getEnergyUnitTree(innerEnergyGroupId)
        .then(res => {
          setTreeNodeData(formatToNodeData(res.list ?? []));
          generateList(formatToNodeData(res.list ?? []));
          setExpandedKeys(dataList.map(i => i.key));
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [innerEnergyGroupId]);

  const onBind = () => {
    const energyGroupId = innerForm.getFieldValue('energyGroupId');
    onChange?.({
      energyUnitGroupId: energyGroupId,
      energyUnitList: unitCheckedKeys.map(item => {
        const found = dataList.find(i => String(i.key) === String(item));
        return {
          energyUnitIds: Number(item),
          energyUnitName: found ? found.title : '',
        };
      }),
    });
    // 同步到外部 form
    form.setFieldValue(`TotalEnergyGroupId${index}`, energyGroupId);
    setVisible(false);
  };
  const mediumOptions = ['电', '水', '气'];
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        <div className={styles.linkText}>{`用${mediumOptions[index]}统计：`}</div>
        <Button
          type="link"
          onClick={() => {
            setVisible(true);
          }}
          disabled={!isEdit}
          style={{ paddingTop: 0 }}
        >
          配置
        </Button>

        <div className={styles.tagStyle}>
          {value?.energyUnitList.map(item => (
            <Tag
              key={item.energyUnitIds}
              closable={isEdit}
              onClose={() => {
                onChange?.({
                  energyUnitGroupId: value?.energyUnitGroupId,
                  energyUnitList: value?.energyUnitList.filter(i => i.energyUnitIds !== item.energyUnitIds) ?? [],
                });
                setUnitCheckedKeys(
                  value?.energyUnitList.filter(i => i.energyUnitIds !== item.energyUnitIds).map(i => i.energyUnitIds)
                );

                if (index === 0) form.setFieldValue(['energyStatisticsUnits', index, 'medium'], undefined);
                else form.setFieldValue(['energyStatisticsUnits', index, 'medium'], []);
              }}
            >
              {item.energyUnitName}
            </Tag>
          ))}
        </div>
      </div>
      <Modal
        title="绑定用能单元1"
        size="small"
        open={visible}
        onCancel={() => {
          setVisible(false);
          // 重置内部 form
          innerForm.resetFields();
        }}
        onOk={onBind}
      >
        <Form form={innerForm}>
          <Form.Item label="用能分析组" name="energyGroupId">
            <Select placeholder="请选择" options={unitGroupOption} />
          </Form.Item>
          <div className={styles.content}>
            <Form.Item
              extra={
                treeData.length > 0 && (
                  <p>
                    {/* @ts-ignore */}
                    <ExclamationCircleOutlined style={{ color: 'var(--warning-color)' }} />
                    灰色选择框表示无该用能单元权限
                  </p>
                )
              }
            >
              <div>
                <Input.Search
                  // style={{ margin: '8px 0px', width: 449 }}
                  placeholder="请输入关键字"
                  onChange={onInputChange}
                />
                <Divider style={{ borderColor: 'rgba(255,255,255,0.15)' }} />

                <Spin spinning={loading}>
                  {treeData.length > 0 ? (
                    <div className={styles.treeBackground} style={{ overflow: 'auto', maxHeight: 367, maxWidth: 433 }}>
                      <Tree
                        checkable
                        checkStrictly
                        // showLine
                        checkedKeys={unitCheckedKeys}
                        onExpand={onExpand}
                        expandedKeys={expandedKeys}
                        autoExpandParent={autoExpandParent}
                        treeData={treeData}
                        onCheck={onCheck}
                      />
                    </div>
                  ) : (
                    <Empty style={{ maxWidth: 433 }} />
                  )}
                </Spin>
              </div>
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default BindUnitModal;
